// WS-16: survey-driven forest (resort.json -> vegetation). Engine-generic and OFF when the config is absent
// (the old baked/random trees are drawn then). Data: tools/vegetation.py -> resorts/<id>/vegetation/
// {vegetation.json, trees.bin}: one tree per crown the orthophoto shows, heights from the lidar canopy model,
// species from crown colour. Schema and method: skill references/landscape.md "Vegetation".
//
// Look: Lapland trees in winter, read from the client's drone photos (refs/photos/winter-ready/Drone Property *):
//  - Scots pine: tall bare trunk (grey below, orange-brown above), a narrow crown of irregular branch clumps in
//    the top 40 %, snow lying on every upward surface;
//  - spruce: a narrow spire from near the ground, drooping tiers, heavily snow-laden;
//  - birch: white trunk, leafless purple-brown twig crown.
// Snow on the crowns is a shader (world-up normal + noise, `snowLoad` 0..1: 0 = dark needles, 1 = "tykky"
// crown snow load), so one geometry serves every look.
//
// Rendering (draw calls matter more than triangles on phones):
//  - low-detail InstancedMeshes per species in a few cells (core 2x2, near band and outer quadrants) for the
//    frustum and shadow box; they are static;
//  - ONE high-detail InstancedMesh per species holds only the trees within `lod.near` x tier tree distance of
//    the camera (3D distance, so a top-down view at height uses the low detail everywhere). It is refilled when
//    the camera has moved; the low meshes collapse those same instances in the vertex shader (uNear), also in
//    the shadow pass, so nothing is drawn twice;
//  - the Battery tier (and phones, lod.mobileThin) draws only the canopy-dominant trees (trees.bin flag 2, ~55 %).
import * as THREE from 'three/webgpu'; // WS-75: WebGPURenderer + node materials
import { Fn, uniform, attribute, texture, uv, varying, float, vec2, vec3, vec4, select, mix, smoothstep, clamp, floor, fract, sin, dot, distance, positionLocal, positionWorld, normalWorld } from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { iCol3 } from './gpu/instance.js';
import { createTreeLayer, prepareAtlas, VegNodeMaterial, instTint } from './trees.js'; // WS-18: tree models, impostors, LOD cross-fade (vegetation.trees)
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from './meshopt.js'; // VIS-239: the kit models are meshopt-compressed (tools/trees/kit_package.py)
import { inRing } from './lines/measure.js'; // VIS-197: the shared inside test of every area
import { degraded } from './degraded.js'; // VIS-27: a quiet failure is reported
import { decodeTrees } from './treebin.js'; // VIS-268: trees.bin format 2 (int32 positions)
import { rawZstdKtx } from './ktx-unzstd.js'; // VIS-267 round 2: the raw KTX2 files' zstd levels unpacked in a worker (unzstdWorker)

const SPECIES = ['pine', 'spruce', 'birch'];

export const fetchBytes = (u, priority = 'auto', signal = undefined) => // (low priority: the opening's own files, the animals and the card photos, go first)
  fetch(u, { priority, signal }).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(`${u}: ${r.status}`))));
// 2026-10-19 tree kits (vegetation.trees.kit = { pack, kinds: { "<code>": "<kit folder>" }, map? }, drawn by lazy/kittrees.js):
// the kit files' bytes, fetched only when main.js finds the key (get = once per file; prefetch = one season's set, low
// priority, from the first picture on). Skill: references/trees.md "Tree kits on the map".
// VIS-195: the kits live in the shared tree pack library, web/trees/<pack>/ (one copy however many maps use a pack; the
// map's own tuning is vegetation.trees.kit.map, laid over kit.json's by lazy/kittrees.js). The folder is resolved like
// an engine module, so a build's import map can name its content-hashed copy (scripts/fingerprint.mjs); without one
// (pnpm dev) it is the folder itself.
// 2026-10-19 (faster map, lane A): kit.json "pictures": "ktx2" = the pictures as KTX2 (kit_package.py; far pictures one
// layer array per kit and map), else WebP; the prefetch reads each kit.json first, then that kit's files of the season.
// ktx(renderer) = the shared KTX2 loader ({ loader, release }: release when the load is done); forget = drop a file's
// bytes once used (a KTX2 parse transfers them to its worker; a later use fetches them again from the HTTP cache).
export const packDir = (pack) => import.meta.resolve(`../trees/${String(pack || '').replace(/[^a-z0-9-]/g, '')}/`);
// VIS-239: stop() = the kit failed (trees.js: attachKit rejected, nothing will read its files): the downloads in flight
// are cancelled, so they stop taking a slow phone's bandwidth, and a season switch fetches nothing more for the visit.
// VIS-239 / VIS-267: { half } (main.js: () => RESCUE, read at the first far file) = the half-size far pictures,
// far_<albedo|normal>_<season>_phone.ktx2 (tools/trees/kit_package.py PHONE_DROP), for the rescue set's 1x chain only.
// The phone set draws at 1.5 device px a CSS px and reads the computers' files: with the half files its nearest pictures
// were magnified 1.5-2x (soft crowns, the coverage dither showing as a stipple; owner 2026-10-25). VIS-267 step 2:
// { astc } (main.js: the renderer's own ASTC feature test, never the device guess; read like `half`) = the files in the
// GPU's own ASTC 6x6 blocks, far_<albedo|normal>_<season>_astc.ktx2 (full size, 25.6 MB a season on the GPU against 57
// transcoded; no transcoder); it beats `half`, which names the half files only where `astc` is off. far(which, season) names them.
// 2026-10-28 (coarse first): the pack holds each far file's coarse copy, far_<albedo|normal>_<season>[_astc]_coarse.ktx2
// (the full file's mip levels from the pack's far.json coarse.drop on, ~1/60 of its bytes; tools/trees/kit_package.py).
// The prefetch asks for the coarse copies beside the near files, at high priority (the map opens with every tree on
// screen from them: lazy/kittrees.js), and the full files come after the first picture at low priority (the kit asks
// for them itself). The rescue set keeps its _phone files (never coarse). far(which, season, { coarse }) names them;
// coarse() says whether the coarse copies are in play; label(season, coarse) = which files draw (the console line and
// the stats: full / coarse / astc / astc coarse / phone); pack = the name they carry.
export function kitFiles(kit, { half = false, astc = false } = {}) {
  const on = (v) => (typeof v === 'function' ? v() : v);
  const coarse = () => !on(half);
  const far = (which, s, { coarse: c = false } = {}) => `far_${which}_${s}${on(astc) ? '_astc' : on(half) ? '_phone' : ''}${c && coarse() ? '_coarse' : ''}.ktx2`;
  const dir = packDir(kit.pack), got = new Map(), ids = Object.values(kit.kinds || {});
  const urlOf = (id, name) => `${dir}${id}/${name}`;
  const ac = new AbortController();
  const get = (id, name, priority = 'auto') => { const u = urlOf(id, name); if (!got.has(u)) { const p = fetchBytes(u, priority, ac.signal); got.set(u, p); p.catch(() => got.delete(u)); } return got.get(u); };
  // VIS-267 lossless (RAM): the bytes of a file once used go (forget); the kit's own files of every season (the model,
  // bark, card data: the first four of `of`) are read once a visit, so a later season's prefetch leaves them once used
  // (it fetched them again and held them for the visit: ~6 MB for the lapland-boreal kits)
  const used = new Set(), forget = (id, name) => { const u = urlOf(id, name); got.delete(u); used.add(u); };
  const of =(s, e) => ['trees.glb', `bark_albedo.${e}`, `bark_normal.${e}`, `cards_data.${e}`, s === 'winter' ? `cards_albedo.${e}` : `cards_albedo_${s}.${e}`, ...(e === 'ktx2' ? [far('albedo', s, { coarse: true }), far('normal', s, { coarse: true })] : [`far_albedo_${s}.${e}`, `far_normal_${s}.${e}`])];
  // (a season the kit has no pictures for, kit.json farPictures.seasons, e.g. spring: only the kit's own files, the first
  // four of `of`, which the kit loads in every season; the map draws today's trees)
  const prefetch = (s) => { if (!ac.signal.aborted) for (const id of ids) get(id, 'kit.json', 'low').then((b) => { const m = JSON.parse(new TextDecoder().decode(b)), e = m.pictures === 'ktx2' ? 'ktx2' : 'webp'; of(s, e).slice(0, (m.farPictures?.seasons || ['winter']).includes(s) ? undefined : 4).forEach((f, j) => { if (j > 3 || !used.has(urlOf(id, f))) get(id, f, j > 4 && coarse() ? 'high' : 'low').catch(() => {}); }); }).catch(() => {}); }; // errors:ignore: a failed file is fetched again where it is used (lazy/kittrees.js reports)
  // (the models: meshopt-compressed and quantized since VIS-239; lazy/kittrees.js bakes them to plain float geometry)
  const label = (s, c) => far('albedo', s, { coarse: c }).replace(/^far_albedo_[a-z]+_?|\.ktx2$/g, '').replace('_', ' ') || 'full';
  return { get, glb: (id) => get(id, 'trees.glb').then((b) => { forget(id, 'trees.glb'); return new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).parseAsync(b, ''); }), prefetch, stop: () => ac.abort(), far, coarse, label,
    ktx:(renderer) => sharedKtx(renderer), forget, pack: kit.pack || '', urlOf };
}
// 2026-10-18 (pine-frost production): ONE KTX2Loader (one transcoder download, one worker pool) for every tree load that
// runs at the same time on a renderer (two sets side by side, e.g. a preview page with the live and the new trees, made two
// loaders: three's 'Multiple active KTX2 loaders' warning, a second transcoder in memory); disposed when the last load ends.
// VIS-267: the package's own KTX2 pictures (the landmarks' glb pictures, the baked material maps: main.js) use it too.
let KTX_SHARED = null;
export async function sharedKtx(renderer) {
  if (!KTX_SHARED || KTX_SHARED.renderer !== renderer) {
    const s = { renderer, users: 0, loader: null };
    s.ready = ktxLoader(renderer).then((l) => (s.loader = l));
    s.release = () => { if (--s.users === 0) { s.loader?.dispose(); if (KTX_SHARED === s) KTX_SHARED = null; } };
    KTX_SHARED = s;
  }
  const s = KTX_SHARED;
  s.users++;
  try { await s.ready; } catch (e) { s.release(); throw e; }
  return s;
}

/**
 * The trees' KTX2Loader (also used by tests/e2e/ktx2-csp.spec.mjs, under the map's enforced CSP). The Basis
 * transcoder runs in a worker; a worker that dies while starting (e.g. string evaluation blocked by the CSP: the
 * vendored glue is patched by scripts/vendor.mjs patchBasisNoEval) answers NOTHING, and three's loader then waits
 * forever. Watchdog: a worker error, or no answer at all within TRANSCODER_WAIT_MS of the first request, rejects every
 * load with an Error marked `ktx2` (trees.js reports it to Sentry; the .webp copies are not deployed, so no fallback).
 */
const TRANSCODER_WAIT_MS = 45_000;
// VIS-255: the rescue set's texture cap. Every tree picture (KTX2: the kits' atlases and far pictures, the forest's) is
// handed out without its `n` largest mip levels: each level dropped is a quarter of the memory (the file's own smaller
// levels, no resampling). main.js sets it once, before the first load (setKtxDrop); 0 = the file as it is. A parse may
// name its own drop (parseAsync's { drop }): the far pictures come at the chain's size already (kitFiles `half`) and are
// never dropped again (VIS-267: the rescue set drew quarter-size far trees, 28 x 56 px a view).
let KTX_DROP = 0;
export function setKtxDrop(n) { KTX_DROP = Math.max(0, Math.floor(n) || 0); }
// VIS-267 step 2: the transcoder workers of every shared loader made from here on (each keeps a WASM heap as large as the
// biggest picture it made: 30-50 MB on a phone); main.js sets it once, before the first load (the phone set: 2). 0 = three's own (4)
let KTX_WORKERS = 0;
export function setKtxWorkers(n) { KTX_WORKERS = Math.max(0, Math.floor(n) || 0); }
/** Drop a compressed texture's `n` largest mip levels (keeps at least one level, and 4 x 4 texels: one block). */
export function dropMips(t, n = KTX_DROP) {
  const m = t?.mipmaps;
  if (!(n > 0) || !Array.isArray(m) || m.length <= 1) return t;
  let k = 0;
  while (k < n && k < m.length - 1 && m[k + 1].width >= 4 && m[k + 1].height >= 4) k++;
  if (!k) return t;
  t.mipmaps = m.slice(k);
  // VIS-267 round 2: a raw file unpacked in the worker (unzstdWorker) has every level in ONE buffer: the kept levels get
  // their own, so the dropped ones really go (a transcoded file's levels have their own buffers already)
  if (m[0].data?.buffer && m[0].data.buffer === t.mipmaps[0].data?.buffer) t.mipmaps = t.mipmaps.map((x) => ({ ...x, data: x.data.slice() }));
  t.image = { ...t.image, width: t.mipmaps[0].width, height: t.mipmaps[0].height };
  return t;
}
/**
 * VIS-267 round 2: unpacks raw KTX2 files' zstd levels in a worker (ktx-unzstd-worker.js) -> { unpack(buf) -> Promise of
 * the same file with supercompression 0 (an ArrayBuffer), close(), ok }. The worker starts at the first file and closes
 * `idleMs` after the last answer (the start's batch of far pictures; a season switch starts it again) or on close(), its
 * decoder's WASM heap with it. The file goes over as a copy: the caller's bytes stay for the fallback. A worker that
 * fails rejects every waiting file and sets ok = false (the loader then reads files on the page, three's own way).
 */
export function unzstdWorker({ make = () => new Worker(new URL('./ktx-unzstd-worker.js', import.meta.url), { type: 'module' }), can = typeof Worker === 'function', idleMs = 1000 } = {}) {
  let w = null, seq = 0, idle = 0;
  const wait = new Map(), me = { ok: !!can }; // can: module workers here (none: every file is read on the page)
  const end = (why) => {
    clearTimeout(idle); idle = 0;
    if (w) { w.terminate(); w = null; }
    for (const f of wait.values()) f.no(new Error(`KTX2 unzstd: ${why}`));
    wait.clear();
  };
  const open = () => {
    if (w) return w;
    w = make();
    w.onmessage = (e) => {
      const f = wait.get(e.data?.id);
      if (!f) return;
      wait.delete(e.data.id);
      if (e.data.error) { me.ok = false; f.no(new Error(`KTX2 unzstd: ${e.data.error}`)); } else f.ok(e.data.buf);
      if (!wait.size) { clearTimeout(idle); idle = setTimeout(() => end('idle'), idleMs); }
    };
    w.onerror = (e) => { e.preventDefault?.(); me.ok = false; end(`worker error: ${e.message || 'failed to start'}`); };
    return w;
  };
  me.unpack = (buf) => new Promise((ok, no) => {
    clearTimeout(idle); idle = 0;
    const id = ++seq;
    wait.set(id, { ok, no });
    try { open().postMessage({ id, buf }); } catch (e) { wait.delete(id); me.ok = false; no(e); }
  });
  me.close = () => end('closed');
  return me;
}
export async function ktxLoader(renderer, waitMs = TRANSCODER_WAIT_MS) {
  const { KTX2Loader } = await import('three/addons/loaders/KTX2Loader.js');
  const ktx = new KTX2Loader().setTranscoderPath(new URL('../vendor/three/addons/libs/basis/', import.meta.url).href).detectSupport(renderer);
  if (KTX_WORKERS) ktx.setWorkerLimit(KTX_WORKERS); // (before the first init: the pool is made there)
  let fail, alive = false, timer = 0;
  const dead = new Promise((_, no) => { fail = (why) => { clearTimeout(timer); no(Object.assign(new Error(`KTX2 transcoder: ${why}`), { ktx2: true })); }; });
  dead.catch(() => {}); // errors:ignore: raced against every load below, which carries the rejection
  const pool = ktx.workerPool, make = () => pool.workerCreator, post = pool.postMessage.bind(pool);
  const init = ktx.init.bind(ktx);
  ktx.init = () => init().then(() => {
    if (make()?.watched) return;
    const create = make(), watched = () => { const w = create(); w.addEventListener('error', (e) => fail(`worker error: ${e.message || 'failed to start'}`)); return w; };
    watched.watched = true;
    pool.setWorkerCreator(watched);
  });
  pool.postMessage = (...a) => {
    if (!alive && !timer) timer = setTimeout(() => fail(`no answer in ${waitMs / 1000} s (the worker did not start)`), waitMs);
    return Promise.race([post(...a).then((r) => { alive = true; clearTimeout(timer); return r; }), dead]);
  };
  // VIS-267 round 2: a raw file with zstd levels (the phones' ASTC far pictures) is unpacked in a short-lived worker first,
  // so three's main-thread zstd decoder (16 MB kept for the visit) is never made; parse is the one door (load, loadAsync,
  // parseAsync and the glTF loader all come through it). If the worker fails, the file is read here as before.
  const unz = unzstdWorker(), parse = ktx.parse.bind(ktx), dispose = ktx.dispose.bind(ktx);
  ktx.parse = (buffer, onLoad, onError) => {
    if (!unz.ok || !rawZstdKtx(buffer)) return parse(buffer, onLoad, onError);
    return unz.unpack(buffer).catch((e) => { console.warn('KTX2: unpacking in a worker failed, unpacked on the page instead', e); return buffer; }).then((b) => parse(b, onLoad, onError));
  };
  ktx.dispose = () => { unz.close(); return dispose(); };
  const loadAsync = ktx.loadAsync.bind(ktx), tag = (url) => (e) => { throw Object.assign(e instanceof Error ? e : new Error(`KTX2 ${url}: ${e?.message || e}`), { ktx2: true }); };
  ktx.loadAsync = (url) => loadAsync(url).then((t) => dropMips(t)).catch(tag(url));
  ktx.parseAsync = (buf, url = 'buffer', { drop = KTX_DROP } = {}) => new Promise((res, rej) => ktx.parse(buf, res, rej)).then((t) => dropMips(t, drop)).catch(tag(url)); // VIS-176: from prefetched bytes; VIS-255: the rescue set's cap (VIS-267: unless the caller names its own drop)
  return ktx;
}


/** Load the vegetation package (vegetation.json + trees.bin). Returns null (with a warning) when missing. */
export async function loadVegetation(base, cfg) {
  const atlasPromise = cfg.trees ? prepareAtlas(cfg.trees.hq) : null; // WS-18: the tree atlas is drawn in a worker while the rest loads (WS-76: hq.birch options)
  try {
    const src = cfg.src || 'vegetation/vegetation.json';
    const meta = await fetch(base + src).then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    const bin = await fetch(base + src.replace(/[^/]*$/, '') + (meta.file || 'trees.bin')).then((r) => { if (!r.ok) throw new Error(r.status); return r.arrayBuffer(); });
    return { meta, cfg, ...decodeTrees(bin, meta), atlasPromise };
  } catch (e) {
    console.warn('vegetation: package not found, run tools/vegetation.py', e);
    degraded('forest', e);
    return null;
  }
}

// ------------------------------------------------------------------ geometry (unit tree: height 1, crown radius 1)
let seed = 5;
const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
const C = (hex) => new THREE.Color(hex);

/** Non-indexed geometry with per-vertex colour (gradient lo->hi over its own height, optional darkening
 *  towards the bottom = cheap self-occlusion) and aSnow (how much snow it can hold: 0 trunk .. 1 crown). */
function part(g, lo, hi, snow, { ao = 0, uvAt = null } = {}) {
  g = g.index ? g.toNonIndexed() : g;
  g.computeBoundingBox();
  const { min, max } = g.boundingBox, pos = g.attributes.position, n = pos.count;
  const col = new Float32Array(n * 3), sn = new Float32Array(n), a = C(lo), b = C(hi), c = new THREE.Color();
  for (let i = 0; i < n; i++) {
    const k = (pos.getY(i) - min.y) / (max.y - min.y || 1);
    c.copy(a).lerp(b, k).multiplyScalar(1 - ao * (1 - k));
    col.set([c.r, c.g, c.b], i * 3);
    sn[i] = snow;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aSnow', new THREE.BufferAttribute(sn, 1));
  if (uvAt) { const uv = new Float32Array(n * 2); for (let i = 0; i < n; i++) uv.set(uvAt, i * 2); g.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); }
  else if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
  g.deleteAttribute('normal');
  return g;
}
const join = (list) => { const g = mergeGeometries(list); g.computeVertexNormals(); return g; };
function trunk(h, r0, r1, seg, lo, hi, opt) { return part(new THREE.CylinderGeometry(r1, r0, h, seg, 1, true).translate(0, h / 2, 0), lo, hi, 0, opt); }
/** An irregular branch clump: a flattened, jittered icosahedron. */
function clump(x, y, z, rx, ry, rz, lo, hi, detail = 0) {
  const g = new THREE.IcosahedronGeometry(1, detail);
  const p = g.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const j = 1 + (rnd() - 0.5) * 0.35;
    p.setXYZ(i, p.getX(i) * j, p.getY(i) * (p.getY(i) < 0 ? 0.55 : 1), p.getZ(i) * j); // flat-bottomed: snow sits on top
  }
  g.scale(rx, ry, rz).rotateY(rnd() * 6.28).translate(x, y, z);
  return part(g, lo, hi, 1, { ao: 0.35 });
}
/** One drooping spruce tier: a jagged open cone (alternating long and short branch tips). */
function tier(y, r, h, seg, lo, hi) {
  const P = [];
  const tip = [0, y + h, 0];
  const ring = [];
  for (let k = 0; k < seg * 2; k++) {
    const a = (k / (seg * 2)) * Math.PI * 2 + rnd() * 0.2, rr = r * (k % 2 ? 0.72 : 1) * (0.9 + rnd() * 0.2);
    ring.push([Math.cos(a) * rr, y - (k % 2 ? 0 : h * 0.12), Math.sin(a) * rr]); // long tips droop
  }
  for (let k = 0; k < ring.length; k++) { const a = ring[k], b = ring[(k + 1) % ring.length]; P.push(...tip, ...b, ...a); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  return part(g, lo, hi, 1, { ao: 0.45 });
}
/** Crossed / tilted quads for the birch twig crown (texture atlas: u < 0.5 side view, u > 0.5 top view). */
function quads(list) {
  const P = [], UV = [];
  for (const { c, w, h, rotY = 0, tilt = 0, top = false } of list) {
    const m = new THREE.Matrix4().makeRotationY(rotY).multiply(new THREE.Matrix4().makeRotationX(tilt));
    const v = [[-w / 2, 0], [w / 2, 0], [w / 2, h], [-w / 2, h]].map(([x, y]) => new THREE.Vector3(x, top ? 0 : y, top ? y - h / 2 : 0).applyMatrix4(m).add(c));
    const u0 = top ? 0.5 : 0, u1 = top ? 1 : 0.5;
    const uv = [[u0, 0], [u1, 0], [u1, 1], [u0, 1]];
    for (const k of [0, 1, 2, 0, 2, 3]) { P.push(v[k].x, v[k].y, v[k].z); UV.push(...uv[k]); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(UV, 2));
  const n = P.length / 3, col = new Float32Array(n * 3).fill(1), sn = new Float32Array(n).fill(0.35);
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('aSnow', new THREE.BufferAttribute(sn, 1));
  return g;
}

function buildGeometries(col) {
  seed = 5;
  const T = 0.075; // trunk radius in crown-radius units (crown r ~1.6 m -> 0.12 m)
  const pineHi = join([
    trunk(0.93, T, T * 0.45, 6, col.barkLow, col.barkHigh),
    ...[[0.56, 0.62], [0.63, 0.78], [0.70, 0.86], [0.77, 0.82], [0.84, 0.70], [0.91, 0.50], [0.97, 0.30]].flatMap(([y, w], i) => {
      const a = i * 2.4, o = 0.18 + rnd() * 0.12;
      return [clump(Math.cos(a) * o, y, Math.sin(a) * o, w, 0.055, w * (0.75 + rnd() * 0.25), col.pineLow, col.pine),
        ...(i < 3 ? [clump(-Math.cos(a) * o * 0.8, y + 0.03, -Math.sin(a) * o * 0.8, w * 0.7, 0.045, w * 0.6, col.pineLow, col.pine)] : [])];
    }),
  ]);
  const pineLo = join([ // 23 triangles: a hexagonal crown mass + a small leader cone
    trunk(0.85, T, T * 0.5, 3, col.barkLow, col.barkHigh),
    part(new THREE.SphereGeometry(1, 6, 2).scale(0.95, 0.17, 0.86).translate(0.04, 0.71, 0), col.pineLow, col.pine, 1, { ao: 0.3 }),
    part(new THREE.ConeGeometry(0.55, 0.2, 5, 1, true).rotateY(0.6).translate(-0.03, 0.9, 0.02), col.pineLow, col.pine, 1, { ao: 0.25 }),
  ]);
  const spruceHi = join([
    trunk(0.2, T * 1.1, T * 0.8, 5, col.barkLow, col.barkLow),
    ...Array.from({ length: 9 }, (_, i) => { const t = 0.05 + i * 0.105; return tier(t, 0.1 + 0.92 * Math.pow(1 - t, 0.95), 0.17, 7, col.spruceLow, col.spruce); }),
  ]);
  const spruceLo = join([
    part(new THREE.ConeGeometry(0.95, 0.5, 6, 1, true).translate(0, 0.3, 0), col.spruceLow, col.spruce, 1, { ao: 0.4 }),
    part(new THREE.ConeGeometry(0.62, 0.45, 6, 1, true).rotateY(0.5).translate(0, 0.62, 0), col.spruceLow, col.spruce, 1, { ao: 0.4 }),
    part(new THREE.ConeGeometry(0.32, 0.3, 5, 1, true).translate(0, 0.86, 0), col.spruceLow, col.spruce, 1, { ao: 0.3 }),
  ]);
  const W = [0.98, 0.03]; // opaque white texel of the twig atlas (bottom-right; CanvasTexture flipY), for the trunk
  const limb = (a, y0, y1, out) => { const g = new THREE.CylinderGeometry(0.02, 0.035, 1, 3, 1, true); const len = Math.hypot(out, y1 - y0); g.translate(0, 0.5, 0).scale(1, len, 1).rotateZ(-Math.atan2(out, y1 - y0)).rotateY(a).translate(0, y0, 0); return part(g, col.birchBark, col.birchBark, 0, { uvAt: W }); };
  const birchHi = join([
    part(new THREE.CylinderGeometry(T * 0.5, T * 0.85, 0.9, 6, 1, true).translate(0, 0.45, 0), col.birchBase, col.birchBark, 0, { uvAt: W }),
    limb(0.3, 0.45, 0.8, 0.35), limb(2.4, 0.5, 0.85, 0.32), limb(4.3, 0.55, 0.9, 0.3),
    quads([0, 1.05, 2.1].map((r) => ({ c: new THREE.Vector3(0, 0.38, 0), w: 2.0, h: 0.66, rotY: r }))
      .concat([{ c: new THREE.Vector3(0, 0.86, 0), w: 1.7, h: 1.7, top: true, tilt: 0.12 }, { c: new THREE.Vector3(0.05, 0.74, 0), w: 1.9, h: 1.9, top: true, tilt: -0.1, rotY: 0.8 }])),
  ]);
  const birchLo = join([
    part(new THREE.CylinderGeometry(T * 0.5, T * 0.85, 0.85, 3, 1, true).translate(0, 0.425, 0), col.birchBase, col.birchBark, 0, { uvAt: W }),
    quads([{ c: new THREE.Vector3(0, 0.4, 0), w: 1.9, h: 0.62, rotY: 0.3 }, { c: new THREE.Vector3(0, 0.4, 0), w: 1.9, h: 0.62, rotY: 1.87 }, { c: new THREE.Vector3(0, 0.8, 0), w: 1.8, h: 1.8, top: true }]),
  ]);
  // far variants (near band + outer quadrants, a few pixels each): one crown mass, 10-14 triangles
  const pineFar = join([trunk(0.8, T, T * 0.5, 3, col.barkLow, col.barkHigh), part(new THREE.SphereGeometry(1, 6, 2) /* hexagonal bipyramid: round from above, 12 tris */.scale(0.8, 0.2, 0.72).translate(0, 0.76, 0), col.pineLow, col.pine, 1, { ao: 0.3 })]);
  const spruceFar = join([part(new THREE.ConeGeometry(0.9, 0.62, 5, 1, true).translate(0, 0.36, 0), col.spruceLow, col.spruce, 1, { ao: 0.4 }), part(new THREE.ConeGeometry(0.5, 0.45, 5, 1, true).rotateY(0.6).translate(0, 0.74, 0), col.spruceLow, col.spruce, 1, { ao: 0.3 })]);
  return { pine: { hi: pineHi, lo: pineLo, far: pineFar }, spruce: { hi: spruceHi, lo: spruceLo, far: spruceFar }, birch: { hi: birchHi, lo: birchLo, far: birchLo } };
}

/** Leafless birch twigs (atlas: left = side view, right = top view, top-right corner = opaque white). */
function twigTexture(color) {
  const c = document.createElement('canvas'); c.width = 512; c.height = 256;
  const g = c.getContext('2d');
  g.strokeStyle = color; g.lineCap = 'round';
  let s = 3;
  const r = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const branch = (x, y, a, len, w, depth) => {
    if (depth === 0 || len < 3) return;
    const x1 = x + Math.cos(a) * len, y1 = y + Math.sin(a) * len;
    g.lineWidth = w; g.beginPath(); g.moveTo(x, y); g.lineTo(x1, y1); g.stroke();
    const n = depth > 3 ? 3 : 2;
    for (let k = 0; k < n; k++) branch(x1, y1, a + (r() - 0.5) * 1.1, len * (0.62 + r() * 0.15), w * 0.68, depth - 1);
  };
  g.globalAlpha = 0.9;
  for (let k = 0; k < 7; k++) branch(128 + (r() - 0.5) * 30, 256, -Math.PI / 2 + (r() - 0.5) * 1.3, 60 + r() * 30, 2.2, 7); // side view: fans up from the stem
  for (let k = 0; k < 9; k++) { const a = (k / 9) * Math.PI * 2 + r() * 0.4; branch(384, 128, a, 40 + r() * 25, 2, 7); } // top view: radial
  g.globalAlpha = 1; g.fillStyle = '#ffffff'; g.fillRect(492, 240, 20, 16);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

// ------------------------------------------------------------------ materials (WS-75: node materials)
const vegHash = Fn(([p0]) => { const p = fract(p0.mul(0.3183099).add(0.1)).mul(17).toVar(); return fract(p.x.mul(p.y).mul(p.z).mul(p.x.add(p.y).add(p.z))); });
const vegNoise = Fn(([x]) => { // value noise (the snow lumps)
  const i = floor(x).toVar(), f0 = fract(x), f = f0.mul(f0).mul(float(3).sub(f0.mul(2))).toVar();
  const h = (dx, dy, dz) => vegHash(i.add(vec3(dx, dy, dz)));
  return mix(mix(mix(h(0, 0, 0), h(1, 0, 0), f.x), mix(h(0, 1, 0), h(1, 1, 0), f.x), f.y), mix(mix(h(0, 0, 1), h(1, 0, 1), f.x), mix(h(0, 1, 1), h(1, 1, 1), f.x), f.y), f.z);
});
// Vertex: (low detail) the near-set collapse, also in the shadow pass (no wind sway: owner 2026-10-27, too heavy to
// process). Per-tree random. Fragment: snow on every upward-facing surface (world normal, flat
// shaded for the conifers), broken up by noise; uSnow = (lower edge, upper edge, amount, per-tree variation), mixed in
// after the vertex and instance colours.
function treeMaterial(U, lo, params) {
  const ip = iCol3.xyz; // instance position (the meshes sit at the origin)
  const collapse = (p) => (lo ? select(U.uNear.w.greaterThan(0).and(distance(ip, U.uNear.xyz).lessThan(U.uNear.w)), ip, p) : p);
  const inst = varying(fract(sin(dot(floor(ip.xz.mul(2)), vec2(12.9898, 78.233))).mul(43758.5453)), 'vgInst');
  const { map: mapTex, ...rest } = params; // the twig atlas is sampled here, not as material.map (the shadow pass would apply its alpha twice)
  const m = new VegNodeMaterial({ roughness: 0.92, side: THREE.DoubleSide, ...rest }, { spec: 0, ownColor: true });
  if (lo) m.positionNode = m.castShadowPositionNode = Fn(() => collapse(positionLocal))();
  const map = mapTex ? texture(mapTex, uv()) : null;
  const base = (map ? map.rgb : vec3(1)).mul(attribute('color', 'vec3')).mul(instTint);
  const W = positionWorld, nz = vegNoise(W.mul(2.1)).mul(0.6).add(vegNoise(W.mul(6.3)).mul(0.4));
  const up = normalWorld.y.add(nz.sub(0.5).mul(0.75)).add(inst.sub(0.5).mul(U.uSnow.w));
  const s0 = smoothstep(U.uSnow.x, U.uSnow.y, up).mul(clamp(attribute('aSnow', 'float'), 0, 1)).mul(U.uSnow.z);
  const s = s0.mul(mix(float(1), smoothstep(0.18, 0.5, vegNoise(W.mul(11))), float(1).sub(U.uSnow.z.mul(U.uSnow.z)).mul(0.75))); // needles show through between snow lumps
  m.colorNode = vec4(mix(base, U.uSnowCol, clamp(s, 0, 1)), map ? map.a : float(1));
  m.name = 'veg-tree'; m.userData.lcFoliage = true; // lazy/lightcast.js: cast light with the foliage response
  return m;
}


/** Look parameters from the config (all optional). snowLoad 0..1 drives the snow edges. */
export function lookOf(cfg = {}) {
  const k = THREE.MathUtils.clamp(cfg.snowLoad ?? 0.45, 0, 1);
  const c = cfg.colors || {};
  return {
    // edges on the world-up normal: k 0.4 -> snow on the flattest ~40 % of the top surfaces; k 0.85 -> crowns white
    snow: new THREE.Vector4(0.98 - 0.9 * k - 0.6 * k * k, 1.28 - 0.9 * k - 0.6 * k * k - 0.1 * k, THREE.MathUtils.lerp(0.55, 1, Math.min(1, k * 1.25)), 0.35),
    snowCol: new THREE.Color(c.snow || '#eef3f8'),
    col: {
      pine: c.pine || '#34442f', pineLow: c.pineLow || '#1c2a1f', spruce: c.spruce || '#2a3d33', spruceLow: c.spruceLow || '#14211b',
      barkLow: c.barkLow || '#4d4038', barkHigh: c.barkHigh || '#9a5f3a', birchBark: c.birchBark || '#e9e7e2', birchBase: c.birchBase || '#4a4440',
      twig: c.twig || 'rgba(112,96,100,0.9)',
    },
  };
}

// ------------------------------------------------------------------ forest
/**
 * o: { scene, veg (loadVegetation), extent, heightAt(x,z) (core), groundY(x,z) (outside the core),
 *      shown(x, z, coin) (treeRules), farShown(x, z), LAND + Landscape (far land cover, optional),
 *      outsideMap(x,z), pipeline, mobile }
 * Returns { update(camera), forEach(fn(x, z, h, r, species)), stats }.
 */
export function createForest(o) {
  const { veg, extent: ex, pipeline } = o;
  const cfg = veg.cfg, look = lookOf(cfg), G = buildGeometries(look.col);
  const U = { uNear: uniform(new THREE.Vector4(0, 0, 0, 0)), uSnow: uniform(look.snow), uSnowCol: uniform(look.snowCol) }; // WS-75: uniform nodes (same .value)
  const twig = twigTexture(look.col.twig);
  const mats = {
    conHi: treeMaterial(U, false, { flatShading: true }),
    conLo: treeMaterial(U, true, { flatShading: true }),
    birHi: treeMaterial(U, false, { map: twig, alphaTest: 0.45, roughness: 1 }),
    birLo: treeMaterial(U, true, { map: twig, alphaTest: 0.45, roughness: 1 }),
  };
  const matOf = (si, lo) => (si === 2 ? (lo ? 'birLo' : 'birHi') : (lo ? 'conLo' : 'conHi'));

  // ---- the tree list: survey trees inside the ortho bounds, then the continuation beyond them
  let X = [], Z = [], H = [], R = [], S = [], D = []; // D = canopy-dominant (kept on Battery)
  const hash = (x, z) => Math.abs(Math.sin(x * 127.1 + z * 311.7) * 43758.5453) % 1;
  const clearAt = cfg.trees ? readabilityClear(cfg.trees.readability, o.places) : null; // WS-18: keep trees off buildings and labels
  // WS-18 r2: no tree grows through a prop or landmark: main.js's inPropClearing(x, z) when it exists (landmarks
  // branch), else a circle of each prop's `clear` radius (resort.json props)
  const propClear = o.clear || propClearFallback(o.props);
  for (let i = 0; i < veg.n; i++) {
    const x = veg.x[i], z = veg.z[i];
    if (o.shown && !(veg.f[i] & 4) && !o.shown(x, z, hash(x, z))) continue; // VIS-199: a planted tree (bit 4) is past the tree rules (main.js plants it)
    if (((clearAt && clearAt(x, z)) || (propClear && propClear(x, z))) && !o.held?.(x, z)) continue; // WS-58 held: kept, hidden until its building moves
    X.push(x); Z.push(z); H.push(veg.h[i]); R.push(veg.r[i]); S.push(veg.s[i]); D.push(veg.f[i] & 2 ? 1 : 0);
  }
  applyTreeOverrides(cfg, { X, Z, H, R, S, D }, hash); // WS-21: photo-sourced species areas + explicit trees (resort.json vegetation)
  const nCore = X.length;
  // WS-31: canopy fills (treeRules mask rules with `fill`, e.g. an off-site zone traced from its orthophoto): trees
  // on a jittered grid at `fill` per 100 m2 wherever the rule shows trees, sizes and species drawn from the survey.
  // The far ring below skips these boxes, so the zone forest is not thinned by the distance taper (round 5: "almost
  // no trees inside the square").
  const fills = (o.fills || []).filter((f) => f.fill > 0 && f.bounds);
  const inFill = (x, z) => fills.some(({ bounds: [x0, z0, x1, z1] }) => x >= x0 && x <= x1 && z >= z0 && z <= z1);
  {
    let s1 = 31;
    const rf = () => ((s1 = (s1 * 16807) % 2147483647) / 2147483647);
    for (const f of fills) {
      const [x0, z0, x1, z1] = f.bounds, step = 10 / Math.sqrt(f.fill);
      for (let gx = x0; gx < x1; gx += step) for (let gz = z0; gz < z1; gz += step) {
        const x = gx + (rf() - 0.5) * step, z = gz + (rf() - 0.5) * step, j = Math.floor(rf() * nCore);
        if (!f.test(x, z) || (clearAt && clearAt(x, z)) || (propClear && propClear(x, z))) continue;
        X.push(x); Z.push(z); H.push(nCore ? H[j] : 10); R.push(nCore ? R[j] : 1.6); S.push(nCore ? S[j] : 0); D.push(rf() < 0.55 ? 1 : 0);
      }
    }
  }
  // Beyond the ortho: land cover (forest / sparse forest) at the survey's own forest density, tapering
  // to `far.share` of it over `far.taper` m, broken into stands by low-frequency noise. Sizes and species
  // are drawn from the survey trees, so nothing changes at the edge but the data source.
  const far = cfg.far || {};
  let farStats = null; // WS-18: hand-over to the landscape's far forest (LAND.treeStats)
  if (o.LAND && far.enabled !== false) {
    const L = o.Landscape, LAND = o.LAND, FOREST = L.CLS.FOREST, SPARSE = L.CLS.SPARSE, sparseK = LAND.cfg.trees?.sparse ?? 0.35;
    const dens = (veg.meta.forestDensityPerHa || 600) / 1e4, taper = far.taper ?? 260, share = far.share ?? 0.1;
    const radius = (o.mobile ? far.radiusMobile : far.radius) ?? LAND.cfg.trees?.radius ?? 1300;
    // a phone's ring is thinner (VIS-233, owner: a thinner far forest is fine): the tries land at random over the whole
    // disc, so stopping at `max` thins it evenly (no hole, no edge)
    const max = (o.mobile ? far.maxMobile : far.max) ?? (o.mobile ? 12000 : 50000);
    const vb = veg.meta.bounds || [ex.minX, ex.minZ, ex.maxX, ex.maxZ];
    const cx = (ex.minX + ex.maxX) / 2, cz = (ex.minZ + ex.maxZ) / 2;
    const outBox = (x, z, b) => Math.max(b[0] - x, x - b[2], b[1] - z, z - b[3]);
    // WS-31: the taper runs from the nearest detailed area (the survey bounds or a canopy-fill box), so a zone's
    // forest continues into its surroundings instead of ending at its box edge
    const out = (x, z) => fills.reduce((d, f) => Math.min(d, Math.max(0.001, outBox(x, z, f.bounds))), outBox(x, z, vb));
    const vn = (x, z) => { // value noise, ~70 m stands
      const fx = x / 70, fz = z / 70, ix = Math.floor(fx), iz = Math.floor(fz), u = fx - ix, v = fz - iz, sm = (t) => t * t * (3 - 2 * t);
      const h2 = (a, b) => hash(a * 13.1, b * 7.7);
      return THREE.MathUtils.lerp(THREE.MathUtils.lerp(h2(ix, iz), h2(ix + 1, iz), sm(u)), THREE.MathUtils.lerp(h2(ix, iz + 1), h2(ix + 1, iz + 1), sm(u)), sm(v));
    };
    let s0 = 99;
    const rr = () => ((s0 = (s0 * 16807) % 2147483647) / 2147483647);
    const tries = Math.round(dens * Math.PI * radius * radius * 1.1);
    let added = 0, t = 0;
    for (; t < tries && added < max; t++) {
      const a = rr() * Math.PI * 2, rad = Math.sqrt(rr()) * radius, x = cx + Math.cos(a) * rad, z = cz + Math.sin(a) * rad;
      const d = out(x, z);
      if (d <= 0 || inFill(x, z)) continue; // WS-31: canopy-fill boxes have their own trees
      const k = L.landClass(LAND, x, z), cover = k === FOREST ? 1 : k === SPARSE ? sparseK : 0;
      if (!cover) continue;
      const tp = share + (1 - share) * (1 - THREE.MathUtils.smoothstep(d, 0, taper));
      const stand = THREE.MathUtils.smoothstep(vn(x, z), 0.18, 0.62); // gaps and denser stands
      // WS-18: with vegetation.trees the ring thins out over the last `fade` m, where the landscape's far forest
      // fades in at the same density (complementary), so there is no edge at any distance
      const edge = cfg.trees ? 1 - THREE.MathUtils.smoothstep(Math.hypot(x - cx, z - cz), radius - (LAND.cfg.trees?.fade ?? 280), radius) : 1;
      const meas = cfg.trees && d > taper * 1.5 && edge > 0.99;
      if (meas) { farStats ??= { tries: 0, kept: 0 }; farStats.tries += cover; }
      if (rr() > cover * tp * (0.35 + 0.9 * stand) * edge) continue;
      if (meas) farStats.kept++;
      if (o.farShown && !o.farShown(x, z)) continue;
      if (propClear && propClear(x, z)) continue; // WS-18 r2
      const j = Math.floor(rr() * nCore);
      X.push(x); Z.push(z); H.push(nCore ? H[j] : 10); R.push(nCore ? R[j] : 1.6); S.push(nCore ? S[j] : 0); D.push(rr() < 0.55 ? 1 : 0);
      added++;
    }
    // stopped at `max`: the ring holds t / tries of its density, so the landscape's far forest beyond takes that too
    // (VIS-233: on a phone it was as dense as on desktop, a denser forest past a thinner ring)
    if (farStats && t < tries) farStats.tries *= tries / Math.max(t, 1);
    // a trail leading out of the survey keeps its forest (`far.along`: lazy/along.js, o.along, desktop only)
    o.along?.({ o, dens, share, taper, t, tries, out, vn, L, LAND, sparseK, radius, cx, cz, inFill, propClear, nCore, X, Z, H, R, S, D });
  }
  if (cfg.trees) { // WS-18: new tree models; this file keeps the data and the placement
    const radius = (o.mobile ? far.radiusMobile : far.radius) ?? o.LAND?.cfg.trees?.radius ?? 1300;
    const dens = farStats && farStats.tries > 50 ? (farStats.kept / farStats.tries) * (veg.meta.forestDensityPerHa || 600) / 1e4 : 0;
    // VIS-199: the editor's frame (o.spare) gets dormant slots at the end, filled live by setPlanted (planted trees)
    const spare = Math.max(0, Math.round(o.spare || 0)), sx = (ex.minX + ex.maxX) / 2, sz = (ex.minZ + ex.maxZ) / 2;
    for (let k = 0; k < spare; k++) { X.push(sx); Z.push(sz); H.push(10); R.push(1.6); S.push(0); D.push(0); }
    // VIS-267 lossless: the lists as typed arrays of the same values (a plain list takes 8 bytes and room to grow per
    // number on a phone's engine; species and flags fit a byte): ~2 MB less on Aurora
    [X, Z, H, R] = [X, Z, H, R].map((a) => Float64Array.from(a)); S = Uint8Array.from(S); D = Uint8Array.from(D);
    return createTreesForest(o, { X, Z, H, R, S, D, nCore, N: X.length, spare, keep: (x, z) => !((clearAt && clearAt(x, z)) || (propClear && propClear(x, z))), handover: o.LAND ? { radius, dens } : null });
  }
  const N = X.length;

  // ---- instance transforms (one master array, for the near-set refills) and colours
  const mats4 = new Float32Array(N * 16), cols = new Float32Array(N * 3), baseY = new Float32Array(N);
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), p = new THREE.Vector3(), sc = new THREE.Vector3(), c = new THREE.Color();
  const inCore = (x, z) => x >= ex.minX && x <= ex.maxX && z >= ex.minZ && z <= ex.maxZ;
  for (let i = 0; i < N; i++) {
    const x = X[i], z = Z[i], r0 = hash(x + 0.31, z), r1 = hash(z, x + 0.77), r2 = hash(x * 1.3, z * 0.7);
    const y = (inCore(x, z) ? o.heightAt(x, z) : o.groundY(x, z)) - 0.25;
    baseY[i] = y;
    e.set((r0 - 0.5) * 0.06, r1 * 6.283, (r2 - 0.5) * 0.06); q.setFromEuler(e);
    // crown radius from the survey spacing, within species limits (Lapland pines and spruces are slender)
    const h = Math.max(2.5, H[i]), r = S[i] === 1 ? Math.min(R[i] * 0.95, 0.15 * h + 0.35) : S[i] === 2 ? Math.min(Math.max(R[i], 0.14 * h + 0.5), 0.2 * h + 0.8) : Math.min(R[i], 0.12 * h + 0.7);
    m4.compose(p.set(x, y, z), q, sc.set(r, h, r));
    m4.toArray(mats4, i * 16);
    const v = 0.86 + r2 * 0.24; // per-tree tint
    c.setRGB(v * (0.96 + r0 * 0.08), v, v * (0.95 + r1 * 0.1));
    c.toArray(cols, i * 3);
  }

  // ---- low-detail cells (static)
  const cx = (ex.minX + ex.maxX) / 2, cz = (ex.minZ + ex.maxZ) / 2;
  const bandOut = (x, z) => Math.max(ex.minX - x, x - ex.maxX, ex.minZ - z, z - ex.maxZ);
  const cellOf = (x, z) => (inCore(x, z) ? `i${x < cx ? 0 : 1}${z < cz ? 0 : 1}` : `${bandOut(x, z) < 350 ? 'b' : 'o'}${Math.floor((Math.atan2(z - cz, x - cx) + Math.PI * 1.25) / (Math.PI / 2)) % 4}`);
  const cells = new Map();
  for (let i = 0; i < N; i++) {
    const key = cellOf(X[i], Z[i]);
    if (!cells.has(key)) cells.set(key, SPECIES.map(() => ({ dom: [], rest: [] })));
    cells.get(key)[S[i]][D[i] ? 'dom' : 'rest'].push(i);
  }
  const lowMeshes = [];
  const lowCap = (n) => n;
  for (const [key, bySp] of cells) {
    bySp.forEach(({ dom, rest }, si) => {
      const ids = dom.concat(rest);
      if (!ids.length) return;
      const farCell = key[0] !== 'i';
      const mesh = new THREE.InstancedMesh(G[SPECIES[si]][farCell ? 'far' : 'lo'], mats[matOf(si, true)], lowCap(ids.length));
      ids.forEach((id, k) => { mesh.instanceMatrix.array.set(mats4.subarray(id * 16, id * 16 + 16), k * 16); });
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(ids.length * 3), 3);
      ids.forEach((id, k) => mesh.instanceColor.array.set(cols.subarray(id * 3, id * 3 + 3), k * 3));
      mesh.computeBoundingBox(); mesh.computeBoundingSphere();
      mesh.castShadow = key[0] === 'i'; // band + outer cells: shadows are a few pixels there; the shadow pass was ~40 % of the tree cost
      mesh.matrixAutoUpdate = false;
      mesh.name = `veg-${key}-${SPECIES[si]}`;
      mesh.userData.nDom = dom.length; mesh.userData.nAll = ids.length;
      o.scene.add(mesh);
      lowMeshes.push(mesh);
    });
  }

  // ---- high-detail near set (dynamic): spatial buckets of 40 m
  // canopy-dominant trees only on the Battery tier, and on phones at any tier (lod.mobileThin: false to keep all)
  const thin = () => pipeline.tier === 'low' || (o.mobile && cfg.lod?.mobileThin !== false);
  const B = 40, buckets = new Map(), bkey = (bx, bz) => (bx + 4096) * 8192 + (bz + 4096); // VIS-270 (B7): numeric keys, as trees.js and kittrees.js
  for (let i = 0; i < N; i++) {
    const k = bkey(Math.floor(X[i] / B), Math.floor(Z[i] / B));
    let l = buckets.get(k); if (!l) buckets.set(k, (l = [])); l.push(i);
  }
  const HI_CAP = o.mobile ? 3000 : 12000;
  const hiMeshes = SPECIES.map((sp, si) => {
    const mesh = new THREE.InstancedMesh(G[sp].hi, mats[matOf(si, false)], HI_CAP);
    mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(HI_CAP * 3), 3);
    mesh.count = 0;
    mesh.castShadow = true;
    mesh.frustumCulled = false; // its bounds change with every refill; it only holds trees near the camera
    mesh.matrixAutoUpdate = false;
    mesh.name = `veg-near-${sp}`;
    o.scene.add(mesh);
    return mesh;
  });
  const last = new THREE.Vector3(1e9, 0, 0);
  let lastTier = null, lastR = -1;
  function countNear(cam, R) {
    const n = [0, 0, 0], lowTier = thin(), R2 = R * R;
    const b0x = Math.floor((cam.x - R) / B), b1x = Math.floor((cam.x + R) / B), b0z = Math.floor((cam.z - R) / B), b1z = Math.floor((cam.z + R) / B);
    for (let bx = b0x; bx <= b1x; bx++) for (let bz = b0z; bz <= b1z; bz++) for (const i of buckets.get(bkey(bx, bz)) || []) {
      if (lowTier && !D[i]) continue;
      const dx = X[i] - cam.x, dy = baseY[i] - cam.y, dz = Z[i] - cam.z;
      if (dx * dx + dy * dy + dz * dz < R2) n[S[i]]++;
    }
    return Math.max(...n);
  }
  function refill(cam, R) {
    const lowTier = thin();
    // the near set never exceeds the high-detail capacity: shrink the radius (every tree is drawn exactly once)
    for (let k = 0; k < 6 && R > 0; k++) { const m = countNear(cam, R); if (m <= HI_CAP) break; R *= Math.sqrt(HI_CAP / m) * 0.97; }
    U.uNear.value.set(cam.x, cam.y, cam.z, R);
    const n = [0, 0, 0];
    if (R > 0) {
      const b0x = Math.floor((cam.x - R) / B), b1x = Math.floor((cam.x + R) / B), b0z = Math.floor((cam.z - R) / B), b1z = Math.floor((cam.z + R) / B);
      const R2 = R * R;
      for (let bx = b0x; bx <= b1x; bx++) for (let bz = b0z; bz <= b1z; bz++) {
        const l = buckets.get(bkey(bx, bz));
        if (!l) continue;
        for (const i of l) {
          if (lowTier && !D[i]) continue;
          const dx = X[i] - cam.x, dy = baseY[i] - cam.y, dz = Z[i] - cam.z;
          if (dx * dx + dy * dy + dz * dz >= R2) continue;
          const si = S[i], mesh = hiMeshes[si], k = n[si];
          if (k >= HI_CAP) continue; // over capacity: the low mesh keeps drawing it... collapsed; rare (cap >> typical)
          mesh.instanceMatrix.array.set(mats4.subarray(i * 16, i * 16 + 16), k * 16);
          mesh.instanceColor.array.set(cols.subarray(i * 3, i * 3 + 3), k * 3);
          n[si] = k + 1;
        }
      }
    }
    hiMeshes.forEach((m, si) => { m.count = n[si]; m.instanceMatrix.needsUpdate = true; m.instanceColor.needsUpdate = true; });
    last.copy(cam);
  }
  function applyTier() {
    const lowTier = thin();
    for (const m of lowMeshes) m.count = lowTier ? m.userData.nDom : m.userData.nAll;
  }
  applyTier();
  const lod = cfg.lod || {};
  return {
    count: N, core: nCore,
    stats: () => ({ trees: N, core: nCore, near: hiMeshes.map((m) => m.count), lowMeshes: lowMeshes.length }),
    forEach(fn) { for (let i = 0; i < N; i++) fn(X[i], Z[i], H[i], R[i], S[i]); },
    forEachCore(fn) { for (let i = 0; i < nCore; i++) fn(X[i], Z[i], H[i], R[i], S[i]); },
    update(cam) {
      if (pipeline.tier !== lastTier) { applyTier(); lastTier = pipeline.tier; last.set(1e9, 0, 0); }
      const R = (pipeline.config.treeNear || 420) * (lod.near ?? 0.22);
      if (R !== lastR || cam.position.distanceToSquared(last) > (R * 0.12) ** 2) { lastR = R; refill(cam.position, R); }
    },
  };
}

// ------------------------------------------------------------------ WS-18: tree models (vegetation.trees)
/** Readability clearings (vegetation.trees.readability = { buildings: m, labels: m }): no trees within that margin
 *  of a place's footprint (its pick mesh) or a label pin. Large areas (paddocks, zones) are never cleared. */
function readabilityClear(rd, places) {
  if (!rd || !places) return null;
  const boxes = [], mb = rd.buildings ?? 0, ml = rd.labels ?? 0;
  for (const [, p] of places) {
    if (p.pin) { if (ml > 0 && p.anchor) boxes.push([p.anchor.x - ml, p.anchor.z - ml, p.anchor.x + ml, p.anchor.z + ml]); continue; }
    const g = p.pick?.geometry; if (!g || mb <= 0) continue;
    if (!g.boundingBox) g.computeBoundingBox();
    const b = g.boundingBox;
    if (b.max.x - b.min.x > (rd.maxSize ?? 70) || b.max.z - b.min.z > (rd.maxSize ?? 70)) continue;
    boxes.push([b.min.x - mb, b.min.z - mb, b.max.x + mb, b.max.z + mb]);
  }
  if (!boxes.length) return null;
  return (x, z) => boxes.some((b) => x > b[0] && x < b[2] && z > b[1] && z < b[3]);
}

// ---- WS-21 (begin): trees from photos newer than the survey -------------------------------------------
// resort.json vegetation.species: [{ polygon: [[x, z]...], species: 'pine'|'spruce'|'birch', share: 0..1, src }]
//   turns `share` of the survey trees inside the polygon into that species (stable per tree).
//   `from: 'birch'` (or a list) changes only trees that are that species now; with `from`, no polygon = the whole
//   survey (e.g. { from: 'birch', species: 'spruce' } = no birch anywhere, then a polygon brings a few back).
// resort.json vegetation.place: [{ x, z, species, height, crown, clear, src }]
//   adds one explicit, canopy-dominant tree (height and crown radius in m) and removes the survey trees within
//   `clear` m (default crown + 1), so a photographed landmark tree replaces the survey's guess at that spot.
// Explicit trees bypass treeRules / readability clearings: they are the authoritative source for that spot.
function applyTreeOverrides(cfg, L, hash) {
  const SP = { pine: 0, spruce: 1, birch: 2 };
  for (const a of cfg.species || []) {
    const s = SP[a.species];
    const from = a.from == null ? null : new Set([a.from].flat().map((n) => SP[n]));
    if (s === undefined || (!Array.isArray(a.polygon) && !from)) continue;
    for (let i = 0; i < L.X.length; i++) if ((!from || from.has(L.S[i])) && (!a.polygon || inRing(L.X[i], L.Z[i], a.polygon)) && hash(L.X[i] + 0.37, L.Z[i] - 1.9) < (a.share ?? 1)) L.S[i] = s;
  }
  const place = (cfg.place || []).filter((t) => Number.isFinite(t.x) && Number.isFinite(t.z) && t.height > 0);
  if (!place.length) return;
  const gone = (x, z) => place.some((t) => Math.hypot(x - t.x, z - t.z) < (t.clear ?? (t.crown ?? 2.5) + 1));
  let k = 0;
  for (let i = 0; i < L.X.length; i++) {
    if (gone(L.X[i], L.Z[i])) continue;
    for (const key of ['X', 'Z', 'H', 'R', 'S', 'D']) L[key][k] = L[key][i];
    k++;
  }
  for (const key of ['X', 'Z', 'H', 'R', 'S', 'D']) L[key].length = k;
  for (const t of place) { L.X.push(t.x); L.Z.push(t.z); L.H.push(t.height); L.R.push(t.crown ?? 2.5); L.S.push(SP[t.species] ?? 0); L.D.push(1); }
}
// ---- WS-21 (end) ----

/** Fallback prop clearing: resort.json props with a `clear` radius (m) and a `pos` [x, z]. */
function propClearFallback(props) {
  const cs = (props || []).filter((p) => p.clear > 0 && Array.isArray(p.pos)).map((p) => [p.pos[0], p.pos[1], (p.clear + 3) ** 2]);
  return cs.length ? (x, z) => cs.some(([px, pz, r2]) => (x - px) ** 2 + (z - pz) ** 2 < r2) : null;
}

function createTreesForest(o, L) {
  const { veg, extent: ex, pipeline } = o, cfg = veg.cfg;
  const inCore = (x, z) => x >= ex.minX && x <= ex.maxX && z >= ex.minZ && z <= ex.maxZ;
  const n = L.N, y = new Float32Array(n), r = new Float32Array(n), h = new Float32Array(n);
  const size = (i) => {
    const x = L.X[i], z = L.Z[i];
    y[i] = (inCore(x, z) ? o.heightAt(x, z) : o.groundY(x, z)) - 0.25;
    h[i] = Math.max(2.5, L.H[i]);
    // crown radius from the survey spacing, within species limits (Lapland pines and spruces are slender), as WS-16
    r[i] = L.S[i] === 1 ? Math.min(L.R[i] * 0.95, 0.15 * h[i] + 0.35) : L.S[i] === 2 ? Math.min(Math.max(L.R[i], 0.14 * h[i] + 0.5), 0.2 * h[i] + 0.8) : Math.min(L.R[i], 0.12 * h[i] + 0.7);
  };
  for (let i = 0; i < n; i++) size(i);
  const cx = (ex.minX + ex.maxX) / 2, cz = (ex.minZ + ex.maxZ) / 2;
  const bandOut = (x, z) => Math.max(ex.minX - x, x - ex.maxX, ex.minZ - z, z - ex.maxZ);
  const cellOf = (x, z) => (inCore(x, z) ? `i${x < cx ? 0 : 1}${z < cz ? 0 : 1}` : `${bandOut(x, z) < 350 ? 'b' : 'o'}${Math.floor((Math.atan2(z - cz, x - cx) + Math.PI * 1.25) / (Math.PI / 2)) % 4}`);
  const layer = createTreeLayer({ scene: o.scene, renderer: o.renderer, pipeline, mobile: o.mobile, storeKey: o.storeKey, atlasData: veg.atlasData, kitTrees: o.kitTrees, cfg: { snowLoad: cfg.snowLoad, ...cfg.trees, snowLevels: cfg.snow }, // WS-18 r3: vegetation.snow = snow-on-trees levels
    // WS-45: every cell casts (was the core only): with the far shadow cascade the band and outer trees are inside
    // the shadowed view, and a core-only rule drew a straight edge where the forest's shadows stopped. The shadow
    // boxes cull the cells they do not reach.
    trees: { n, x: L.X, z: L.Z, y, h, r, s: L.S, dom: L.D }, cellOf, castShadowCell: () => true, spare: L.spare });
  const S0 = n - (L.spare || 0), layerPlanted = layer.setPlanted; // (the layer's own: the slots, written below)
  return Object.assign(layer, {
    count: n, core: L.nCore, handover: L.handover,
    /** VIS-199 (the editor's frame): the planted trees `t` (lazy/touches.js plantedTrees) into the spare slots, past the
     *  buildings' readability boxes and the props' clearings as every tree. { shown, over } (over: more than the slots). */
    setPlanted(t) {
      if (!L.spare) return { shown: 0, over: !!t?.n };
      let k = 0, j = 0;
      for (; j < (t?.n || 0) && k < L.spare; j++) {
        if (!L.keep(t.x[j], t.z[j])) continue;
        const i = S0 + k++;
        L.X[i] = t.x[j]; L.Z[i] = t.z[j]; L.H[i] = t.h[j]; L.R[i] = t.r[j]; L.S[i] = t.s[j]; L.D[i] = t.f[j] & 2 ? 1 : 0;
        size(i);
      }
      return { shown: layerPlanted(k), over: j < (t?.n || 0) };
    },
    forEach(fn) { for (let i = 0; i < n; i++) fn(L.X[i], L.Z[i], L.H[i], L.R[i], L.S[i]); },
    forEachCore(fn) { for (let i = 0; i < L.nCore; i++) fn(L.X[i], L.Z[i], L.H[i], L.R[i], L.S[i]); },
  });
}
